/**
 * 通知工具函数集合
 * 包含音频播放和桌面通知功能
 */

import {
    hasNotificationPermission,
    isNotificationSupported,
    requestGlobalNotificationPermission,
    getGlobalNotificationState
} from './globalNotificationManager';

export interface NotificationConfig {
    soundEnabled?: boolean;
    desktopNotificationEnabled?: boolean;
    volume?: number;
}

export interface SoundOptions {
    soundFile?: string;
    volume?: number;
    fallbackSounds?: string[];
}

export interface DesktopNotificationOptions {
    title: string;
    body: string;
    icon?: string;
    badge?: string;
    tag?: string;
    requireInteraction?: boolean;
    silent?: boolean;
    autoCloseDelay?: number;
    onClick?: () => void;
    data?: any;
}

/**
 * 获取用户选择的提示音
 */
export const getSelectedSound = (): string => {
    return localStorage.getItem('notificationSound') || '1';
};

/**
 * 获取桌面通知设置
 */
export const getDesktopNotificationEnabled = (): boolean => {
    const saved = localStorage.getItem('desktopNotificationEnabled');
    return saved !== 'false';
};

/**
 * 保存通知设置
 */
export const saveNotificationSettings = (soundFile: string, desktopEnabled: boolean): void => {
    localStorage.setItem('notificationSound', soundFile);
    localStorage.setItem('desktopNotificationEnabled', desktopEnabled.toString());
};

/**
 * 请求通知权限（使用全局管理器）
 */
export const requestNotificationPermission = async (): Promise<NotificationPermission> => {
    return await requestGlobalNotificationPermission();
};

/**
 * 播放提示音
 */
export const playNotificationSound = async (options: SoundOptions = {}): Promise<void> => {
    const {
        soundFile = getSelectedSound(),
        volume = 1.0,
        fallbackSounds = ['/y1897.mp3', '/1754276079282.mp3']
    } = options;

    try {
        // 构建可能的音频路径
        const possiblePaths = [
            `/${soundFile}.mp3`,
            `/public/${soundFile}.mp3`,
            `./public/${soundFile}.mp3`,
            ...fallbackSounds
        ];

        let audioLoaded = false;
        const audio = new Audio();

        // 尝试加载音频文件
        const tryLoadAudio = (pathIndex: number): Promise<void> => {
            return new Promise((resolve, reject) => {
                if (pathIndex >= possiblePaths.length) {
                    reject(new Error(`所有音频路径都无法加载 (用户选择: ${soundFile})`));
                    return;
                }

                const path = possiblePaths[pathIndex];
                audio.src = path;
                audio.volume = volume;

                audio.onloadstart = () => {
                    console.log(`尝试加载音频: ${path} (用户选择: ${soundFile})`);
                };

                audio.oncanplaythrough = () => {
                    console.log(`音频加载成功: ${path} (用户选择: ${soundFile})`);
                    audioLoaded = true;
                    audio.play()
                        .then(() => resolve())
                        .catch((error) => {
                            console.warn('音频播放失败:', error);
                            reject(error);
                        });
                };

                audio.onerror = () => {
                    console.warn(`音频加载失败: ${path} (用户选择: ${soundFile})`);
                    // 尝试下一个路径
                    tryLoadAudio(pathIndex + 1).then(resolve).catch(reject);
                };

                audio.load();
            });
        };

        // 开始尝试加载
        await tryLoadAudio(0);
    } catch (error) {
        console.warn('音频播放失败，尝试使用浏览器内置提示音:', error);
        // 使用浏览器内置提示音作为最后的备选
        try {
            await playBrowserNotificationSound();
        } catch (browserError) {
            console.error('浏览器内置提示音也播放失败:', browserError);
        }
    }
};

/**
 * 播放浏览器内置提示音
 */
export const playBrowserNotificationSound = async (): Promise<void> => {
    try {
        // 创建一个简短的音频上下文来播放提示音
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.frequency.setValueAtTime(800, audioContext.currentTime); // 800Hz 提示音
        oscillator.type = 'sine';

        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime); // 设置音量
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);

        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.1);

        // 清理资源
        setTimeout(() => {
            audioContext.close();
        }, 200);
    } catch (error) {
        throw new Error(`播放浏览器内置提示音失败: ${error}`);
    }
};

/**
 * 显示桌面通知
 */
export const showDesktopNotification = (options: DesktopNotificationOptions): Notification | null => {
    const {
        title,
        body,
        icon = '/favicon.ico',
        badge = '/favicon.ico',
        tag = 'notification',
        requireInteraction = false,
        silent = false,
        autoCloseDelay = 3000,
        onClick,
        data
    } = options;

    if (!isNotificationSupported()) {
        console.warn('浏览器不支持通知API');
        return null;
    }

    if (!hasNotificationPermission()) {
        console.warn('没有通知权限');
        return null;
    }

    if (!getDesktopNotificationEnabled()) {
        console.log('桌面通知已被用户禁用');
        return null;
    }

    try {
        const notification = new Notification(title, {
            body,
            icon,
            badge,
            tag,
            requireInteraction,
            silent,
            data
        });

        // 点击通知时的处理
        notification.onclick = () => {
            window.focus();
            notification.close();
            onClick?.();
        };

        // 自动关闭通知
        if (autoCloseDelay > 0) {
            setTimeout(() => {
                notification.close();
            }, autoCloseDelay);
        }

        return notification;
    } catch (error) {
        console.error('显示桌面通知失败:', error);
        return null;
    }
};

/**
 * 综合通知函数 - 同时播放声音和显示桌面通知
 */
export const sendNotification = async (
    desktopOptions: DesktopNotificationOptions,
    soundOptions: SoundOptions = {},
    config: NotificationConfig = {}
): Promise<{
    sound: boolean;
    desktop: Notification | null;
}> => {
    const {
        soundEnabled = true,
        desktopNotificationEnabled = getDesktopNotificationEnabled()
    } = config;

    const results = {
        sound: false,
        desktop: null as Notification | null
    };

    // 播放声音
    if (soundEnabled) {
        try {
            await playNotificationSound(soundOptions);
            results.sound = true;
        } catch (error) {
            console.warn('声音播放失败:', error);
        }
    }

    // 显示桌面通知
    if (desktopNotificationEnabled) {
        results.desktop = showDesktopNotification(desktopOptions);
    }

    return results;
};

/**
 * 新聊天室通知
 */
export const notifyNewChatRoom = async (roomCount: number): Promise<void> => {
    await sendNotification(
        {
            title: '新的聊天室',
            body: `您有新的聊天室消息，当前共有 ${roomCount} 个聊天室`,
            tag: 'chat-room-notification',
            autoCloseDelay: 30000
        },
        {},
        {
            soundEnabled: true,
            desktopNotificationEnabled: getDesktopNotificationEnabled()
        }
    );
};

/**
 * 新聊天消息通知
 */
export const notifyNewMessage = async (message: string, sender?: string): Promise<void> => {
    const title = sender ? `来自 ${sender} 的新消息` : '新的聊天消息';
    const body = message.length > 50 ? message.substring(0, 50) + '...' : message;

    await sendNotification(
        {
            title,
            body,
            tag: 'chat-message-notification',
            autoCloseDelay: 5000,
            data: { sender }
        },
        {},
        {
            soundEnabled: true,
            desktopNotificationEnabled: getDesktopNotificationEnabled()
        }
    );
};

/**
 * 测试通知
 */
export const testNotification = async (): Promise<void> => {
    await sendNotification(
        {
            title: '测试通知',
            body: '这是一个测试桌面通知',
            tag: 'test-notification',
            autoCloseDelay: 3000
        },
        { soundFile: getSelectedSound() },
        {
            soundEnabled: true,
            desktopNotificationEnabled: true
        }
    );
};
